import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedEvent, EventInput, EventResult } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface EventStore {
  events: SavedEvent[]
  addEvent: (event: Omit<SavedEvent, 'id' | 'createdAt' | 'updatedAt'>) => void
  addBatchEvents: (events: Omit<SavedEvent, 'id' | 'createdAt' | 'updatedAt'>[]) => void
  updateEvent: (id: string, event: Partial<SavedEvent>) => void
  removeEvent: (id: string) => void
  getEvent: (id: string) => SavedEvent | undefined
  toggleFavorite: (id: string) => void
  markAsExecuted: (id: string, executionNotes?: string) => void
  updatePlayerChoice: (id: string, choiceId: string, optionId: string) => void
  addRating: (id: string, rating: number, feedback?: string) => void
  exportAsText: () => void
  exportAsJSON: () => void
  exportSingleAsText: (id: string) => void
  exportSingleAsJSON: (id: string) => void
  importFromJSON: (jsonString: string) => void
  fixDuplicateIds: () => boolean
  clearAll: () => void
  clearEvents: () => void
  getEventsByWorld: (worldId: string) => SavedEvent[]
  getEventsByCampaign: (campaignId: string) => SavedEvent[]
  getChildEvents: (parentEventId: string) => SavedEvent[]
  getEventSequence: (sessionId: string) => SavedEvent[]
}

const MAX_EVENTS = 300

export const useEventStore = create<EventStore>()(
  persist(
    (set, get) => ({
      events: [],

      addEvent: (event) => {
        const newEvent: SavedEvent = {
          ...event,
          id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }

        set(state => {
          const events = [newEvent, ...state.events]
          if (events.length > MAX_EVENTS) {
            events.pop()
          }
          return { events }
        })
      },
      
      addBatchEvents: (batchEvents) => {
        const newEvents: SavedEvent[] = batchEvents.map((event, index) => ({
          ...event,
          // バッチ保存時のID重複防止（50ms遅延相当の処理）
          id: `${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        }))

        set(state => {
          let events = [...newEvents, ...state.events]
          if (events.length > MAX_EVENTS) {
            events = events.slice(0, MAX_EVENTS)
          }
          return { events }
        })
      },

      updateEvent: (id, updates) => {
        set(state => ({
          events: state.events.map(event =>
            event.id === id
              ? { ...event, ...updates, updatedAt: new Date().toISOString() }
              : event
          )
        }))
      },

      removeEvent: (id) => {
        set(state => ({
          events: state.events.filter(event => event.id !== id)
        }))
      },

      getEvent: (id) => {
        return get().events.find(event => event.id === id)
      },

      toggleFavorite: (id) => {
        set(state => ({
          events: state.events.map(event =>
            event.id === id
              ? { ...event, favorite: !event.favorite, updatedAt: new Date().toISOString() }
              : event
          )
        }))
      },

      markAsExecuted: (id, executionNotes) => {
        set(state => ({
          events: state.events.map(event =>
            event.id === id
              ? {
                  ...event,
                  executed: true,
                  executionDate: new Date().toISOString(),
                  executionNotes: executionNotes || event.executionNotes,
                  updatedAt: new Date().toISOString()
                }
              : event
          )
        }))
      },

      updatePlayerChoice: (id, choiceId, optionId) => {
        set(state => ({
          events: state.events.map(event =>
            event.id === id
              ? {
                  ...event,
                  playerChoices: {
                    ...event.playerChoices,
                    [choiceId]: optionId
                  },
                  updatedAt: new Date().toISOString()
                }
              : event
          )
        }))
      },

      addRating: (id, rating, feedback) => {
        set(state => ({
          events: state.events.map(event =>
            event.id === id
              ? {
                  ...event,
                  rating,
                  feedback: feedback || event.feedback,
                  usageCount: (event.usageCount || 0) + 1,
                  updatedAt: new Date().toISOString()
                }
              : event
          )
        }))
      },
      
      // ID重複をチェックして修正する関数
      fixDuplicateIds: () => {
        const { events } = get()
        const idMap = new Map<string, number>()
        const duplicates: string[] = []
        
        // 重複IDを検出
        events.forEach(event => {
          const count = idMap.get(event.id) || 0
          idMap.set(event.id, count + 1)
          if (count > 0) {
            duplicates.push(event.id)
          }
        })
        
        if (duplicates.length === 0) {
          return false
        }
        
        // 重複IDを修正
        set(state => ({
          events: state.events.map(event => {
            if (duplicates.includes(event.id)) {
              return {
                ...event,
                id: `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
                updatedAt: new Date().toISOString()
              }
            }
            return event
          })
        }))
        
        return true
      },

      exportAsText: () => {
        const { events } = get()
        if (events.length === 0) return

        const text = events.map(event => {
          const lines = [
            `【イベント: ${event.result.basicInfo.name}】`,
            `タイプ: ${event.result.basicInfo.type}`,
            `カテゴリ: ${event.result.basicInfo.category}`,
            `概要: ${event.result.basicInfo.summary}`,
            '',
            '■ 詳細説明',
            event.result.basicInfo.description,
            '',
            '■ 発生条件',
            `トリガー: ${event.result.basicInfo.trigger}`,
            event.result.basicInfo.prerequisites?.length 
              ? `前提条件: ${event.result.basicInfo.prerequisites.join(', ')}`
              : '',
            '',
            '■ シナリオ',
            event.result.scenario.opening?.scene || '',
            event.result.scenario.development?.mainEvents?.map(e => 
              `- ${e.description}`
            ).join('\n') || '',
            event.result.scenario.climax?.description || '',
            '',
            '■ バリエーション',
            event.result.scenario.variations?.map(v => 
              `${v.name}: ${v.description}`
            ).join('\n') || '',
            '',
            '■ 報酬',
            `経験値: ${event.result.consequences?.rewards?.experience || 'なし'}`,
            `アイテム: ${event.result.consequences?.rewards?.items?.join(', ') || 'なし'}`,
            '',
            `生成日: ${new Date(event.createdAt).toLocaleString('ja-JP')}`,
            event.memo ? `メモ: ${event.memo}` : '',
            event.executed ? `実行済み: ${event.executionDate}` : '',
            event.rating ? `評価: ${'★'.repeat(event.rating)}` : '',
            '─'.repeat(50)
          ].filter(Boolean)

          return lines.join('\n')
        }).join('\n\n')

        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `events_${new Date().toISOString().split('T')[0]}.txt`
        a.click()
        URL.revokeObjectURL(url)
      },

      exportAsJSON: () => {
        const { events } = get()
        if (events.length === 0) return

        const json = JSON.stringify(events, null, 2)
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `events_${new Date().toISOString().split('T')[0]}.json`
        a.click()
        URL.revokeObjectURL(url)
      },

      exportSingleAsText: (id) => {
        const event = get().events.find(e => e.id === id)
        if (!event) return

        const lines = [
          `【イベント: ${event.result.basicInfo.name}】`,
          `タイプ: ${event.result.basicInfo.type}`,
          `カテゴリ: ${event.result.basicInfo.category}`,
          '',
          '■ 基本情報',
          `概要: ${event.result.basicInfo.summary}`,
          `説明: ${event.result.basicInfo.description}`,
          `トリガー: ${event.result.basicInfo.trigger}`,
          '',
          '■ シナリオ',
          '【オープニング】',
          event.result.scenario.opening?.scene || '',
          '',
          '【展開】',
          event.result.scenario.development?.mainEvents?.map((e, i) => 
            `${i + 1}. ${e.description}`
          ).join('\n') || '',
          '',
          '【クライマックス】',
          event.result.scenario.climax?.description || '',
          '',
          '■ バリエーション',
          event.result.scenario.variations?.map((v, i) => [
            `【バリエーション${i + 1}: ${v.name}】`,
            `説明: ${v.description}`,
            `違い: ${v.difference}`,
            `固有要素: ${v.uniqueElements?.join(', ') || 'なし'}`,
            ''
          ].join('\n')).join('\n') || '',
          '',
          '■ 選択肢',
          event.result.choices?.decisionPoints?.map(dp => [
            `【${dp.timing}】`,
            `状況: ${dp.situation}`,
            '選択肢:',
            ...dp.options.map(opt => 
              `  - ${opt.label}: ${opt.description}`
            ),
            ''
          ].join('\n')).join('\n') || '',
          '',
          '■ 報酬と結果',
          `経験値: ${event.result.consequences?.rewards?.experience || 'なし'}`,
          `アイテム: ${event.result.consequences?.rewards?.items?.join(', ') || 'なし'}`,
          `知識: ${event.result.consequences?.rewards?.knowledge?.join(', ') || 'なし'}`,
          '',
          '■ メタ情報',
          `難易度: ${event.result.metadata?.difficulty || '不明'}/10`,
          `推奨レベル: ${event.result.metadata?.recommendedLevel || '不明'}`,
          `プレイ時間: ${event.result.metadata?.playTime || '不明'}`,
          `プレイヤー数: ${event.result.metadata?.playerCount || '不明'}`,
          '',
          `生成日: ${new Date(event.createdAt).toLocaleString('ja-JP')}`,
          event.memo ? `メモ: ${event.memo}` : ''
        ].filter(Boolean)

        const text = lines.join('\n')
        const blob = new Blob([text], { type: 'text/plain;charset=utf-8' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `event_${event.result.basicInfo.name.replace(/[^a-zA-Z0-9ぁ-んァ-ヶー一-龠]/g, '_')}.txt`
        a.click()
        URL.revokeObjectURL(url)
      },

      exportSingleAsJSON: (id) => {
        const event = get().events.find(e => e.id === id)
        if (!event) return

        const json = JSON.stringify(event, null, 2)
        const blob = new Blob([json], { type: 'application/json' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `event_${event.result.basicInfo.name.replace(/[^a-zA-Z0-9ぁ-んァ-ヶー一-龠]/g, '_')}.json`
        a.click()
        URL.revokeObjectURL(url)
      },

      importFromJSON: (jsonString) => {
        try {
          const imported = JSON.parse(jsonString)
          const events = Array.isArray(imported) ? imported : [imported]
          
          const validEvents = events.filter(e => 
            e.result?.basicInfo?.name && 
            e.input
          ).map(e => ({
            ...e,
            id: e.id || `${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
            createdAt: e.createdAt || new Date().toISOString(),
            updatedAt: new Date().toISOString()
          }))

          set(state => {
            let newEvents = [...validEvents, ...state.events]
            if (newEvents.length > MAX_EVENTS) {
              newEvents = newEvents.slice(0, MAX_EVENTS)
            }
            return { events: newEvents }
          })
        } catch (error) {
          console.error('Failed to import events:', error)
          alert('インポートに失敗しました。正しいJSONファイルか確認してください。')
        }
      },

      clearAll: () => {
        if (confirm('すべてのイベントデータを削除します。よろしいですか？')) {
          set({ events: [] })
        }
      },

      clearEvents: () => {
        set({ events: [] })
      },

      getEventsByWorld: (worldId) => {
        return get().events.filter(event => event.worldId === worldId)
      },

      getEventsByCampaign: (campaignId) => {
        return get().events.filter(event => event.campaignId === campaignId)
      },

      getChildEvents: (parentEventId) => {
        return get().events.filter(event => event.parentEventId === parentEventId)
      },

      getEventSequence: (sessionId) => {
        return get().events
          .filter(event => event.sessionId === sessionId)
          .sort((a, b) => (a.sequenceNumber || 0) - (b.sequenceNumber || 0))
      }
    }),
    {
      name: 'event-store',
      storage: createIndexedDBStorage('event-store'),
      version: 1,
    }
  )
)